import { Injectable, signal } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { GUESTS_SEED } from '@common/data';
import type { Guest, GuestPage, GuestQuery } from '@common/types';

const LIST_LATENCY_MS = 5000;
const DETAIL_LATENCY_MS = 5000;
const SAVE_LATENCY_MS = 300;

@Injectable({ providedIn: 'root' })
export class GuestsService {
  private readonly guests = signal<Guest[]>(GUESTS_SEED.slice());
  private readonly _revision = signal<number>(0);

  readonly all = this.guests.asReadonly();
  readonly revision = this._revision.asReadonly();

  list(query: GuestQuery): Observable<GuestPage> {
    let result: Guest[] = this.guests().slice();

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (g) =>
          g.name.toLowerCase().includes(term) ||
          g.email.toLowerCase().includes(term) ||
          g.room.toLowerCase().includes(term),
      );
    }

    const statusFilters = query.filters['status'];
    if (statusFilters?.length) {
      const allowed = new Set(statusFilters);
      result = result.filter((g) => allowed.has(g.status));
    }

    if (query.sortField) {
      const field = query.sortField as keyof Guest;
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      result.sort((a, b) => {
        const av = a[field];
        const bv = b[field];
        if (av < bv) return -1 * direction;
        if (av > bv) return 1 * direction;
        return 0;
      });
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const end = start + Math.max(1, query.rows);
    const data = result.slice(start, end);

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }

  getById(id: string): Observable<Guest | undefined> {
    return of(this.guests().find((g) => g.id === id)).pipe(delay(DETAIL_LATENCY_MS));
  }

  delete(id: string): Observable<void> {
    const existing = this.guests().find((g) => g.id === id);
    if (!existing) {
      return throwError(() => new Error('Guest not found.'));
    }
    this.guests.update((rows) => rows.filter((g) => g.id !== id));
    this._revision.update((n) => n + 1);
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }
}
